import { IDocument, LayoutConfigOption, TagFilter } from "@vp/models";
import { JSONSchema7 } from "json-schema";
import { PatientConsoleDeviceData } from "./patient-console-device-data";
import { Profile } from "./profile";
import { UserRole } from "./user-role";

export interface User extends IDocument {
  userId: string;
  userType: UserType;
  isOnline: boolean;
  email: string;
  profile: Profile;
  roles: UserRole[];
  active: boolean;
  tags: string[];
  assignedTags: string[];
  selectedRoleId: string;
  lastLoginDateTime?: string;
  /** Server-stamped when the user's last SignalR connection drops; shown as "Last seen". */
  lastSeenDateTime?: string;
  invitationEmail?: UserInvitationEmail;
  userData?: UserData;
  patientConsoleDeviceData?: PatientConsoleDeviceData;
  deviceAssignmentData?: DeviceAssignmentData | null;
  acesoTvDeviceData?: AcesoTvDeviceData;
  userPermissions: UserPermissions[];
  callData?: CallData;
  metaData?: Record<string, unknown>;
}

export interface DeviceAssignmentData {
  currentAssignment: DeviceAssignment;
  previousAssignment: DeviceAssignment;
}

export interface DeviceAssignment {
  caseId: string | null;
  location: string | null;
}

export interface AcesoTvDeviceData {
  camera: boolean;
  channel: number;
  power: "off" | "on"; // represents the TV glass state.
  volume: number;
  glass: "off" | "on"; // represents the screen visible state
  blackout: "off" | "on"; // represents the blackout curtain state.
  pan: number;
  tilt: number;
  zoom: number;
}

export interface UserInvitationEmail {
  subject: string;
  body: string;
}

export interface UserPermissions {
  roleId: string;
  moduleId: string;
  permissionSets: string[];
}

export interface TagsDeleted {
  [key: string]: string[];
}

export interface UserType {
  userTypeId: string;
  friendlyId: UserTypeFriendlyId;
  displayName: string;
}

export enum UserTypeFriendlyId {
  loginUser = "login-user",
  nonLoginUser = "non-login-user",
  device = "device",
  acesoTv = "aceso-tv"
}

export interface UserData {
  acknowledgedAlerts?: string[];
  snippets?: Snippet[];
  tagFilters?: TagFilter[];
  flags: { [key: string]: boolean };
  [key: string]: any;
  informaticistProfile?: InformaticistProfile;
  /** The user's own availability; absent means Admin. */
  availability?: UserAvailability;
  permissions: string[];
  userPermissions: UserPermissions[];
  lastModuleVisited: string;
}

/** The statuses a user picks by hand. Busy is not one: it derives from `activeCase`. */
export type AvailabilityStatus = "ready" | "admin";

/**
 * A user's availability, stored at `userData.availability`. The client writes
 * only `value`; the server stamps `since` whenever `value` changes and owns
 * `activeCase`. Whether the user is online comes from presence, not from here.
 */
export interface UserAvailability {
  value: AvailabilityStatus;
  /** ISO UTC instant `value` last changed. */
  since?: string;
  /** The call the user is on. While set, the user shows as Busy whatever `value` is. */
  activeCase?: ActiveCaseRef | null;
}

/** The case behind a user's Busy status. */
export interface ActiveCaseRef {
  caseId: string;
  /** Null for cases that never received a reference number. */
  referenceNumber: string | null;
  /** ISO UTC instant the call started. */
  since: string;
}

export interface Snippet {
  title: string;
  text: string;
}

export interface InformaticistProfile {
  available: boolean;
  assignedFacilities: string[];
  assignedTopics: string[];
  /** Topic tag ids the user is an SME on. Always a subset of `assignedTopics`. */
  smeTopics: string[];
  /** Topic tag ids the user is actively covering this shift. Subset of `assignedTopics`. */
  activeTopics: string[];
  /** ISO timestamp of the last manager-authored coverage save (assigned + SME). */
  assignedUpdatedAt?: string;
  /** ISO timestamp of the last active-shift selection save. */
  activeUpdatedAt?: string;
}

/**@deprecated
 * usages of this should be replaced with just the id of the user and resolved when used.
 * */
export interface UserLite {
  email: string;
  firstName: string;
  lastName: string;
  salutation: string;
  userId: string;
  isOnline: boolean;
  deviceAssignmentData?: DeviceAssignmentData | null;
}

export interface Flag {
  [key: string]: boolean;
}

export interface UserTypeConfig {
  type: string;
  singleRoleAssignment: boolean;
  singleDepartmentAssignment: boolean;
  assignableRoleFriendlyId: string[];
  assignableDepartmentFriendlyId: string[];
  userSchema: JSONSchema7;
  userLayout: LayoutConfigOption;
}

export interface CallData {
  zoomAuthenticated?: boolean;
  active: boolean;
  applicationHostId: string;
  topic: string;
  lastUpdatedBy: string;
  lastUpdatedDateTime: string;
}
